import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  parseAtlasPdf,
  submitAtlasInkooporder,
  AtlasInkooporderError,
  type AtlasParseResult,
  type AtlasParsedItem,
} from '../../lib/atlas-inkooporder';
import { ATLAS_PRODUCT_CODES, resolveAtlasProductCode } from '../../lib/b2b-accounts';
import { afrondenEnFactureren, B2BCyclusError, isB2BCyclusOpenStatus } from '../../lib/b2b-cyclus';
import { B2BRetourlabelButton } from './B2BRetourlabelButton';

/** Subset van OrderInfo uit de klanten-store die de afrondstap nodig heeft. */
export interface CycleOrder {
  id: string;
  reference: string;
  status: string;
  total: number;
  createdDate: string;
}

interface AtlasInkooporderWizardDialogProps {
  accountId: string;
  accountName: string;
  isOpen: boolean;
  /** Orders van de klant; de done-stap filtert zelf op "Wordt gebracht". */
  openCycleOrders?: CycleOrder[];
  onClose: () => void;
  onCreated: (workOrderId: string, workOrderNumber: string, itemsCreated: number) => void;
  onOpenOrder?: (workOrderId: string) => void;
}

type WizardState = 'idle' | 'parsing' | 'preview' | 'submitting' | 'error' | 'done';

type AfrondEntry =
  | { status: 'busy' }
  | { status: 'done'; invoiceNaam: string | null; sent: string }
  | { status: 'error'; message: string };

interface EditableItem extends AtlasParsedItem {
  // canonical (prefixed) productCode voor whitelist-check; raw uit AI blijft in productCode.
  canonicalCode: string | null;
}

function formatEur(value: number | undefined | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(value);
}

function toEditable(items: AtlasParsedItem[]): EditableItem[] {
  return items.map((it) => ({
    ...it,
    canonicalCode: resolveAtlasProductCode(it.productCode),
  }));
}

export function AtlasInkooporderWizardDialog({
  accountId,
  accountName,
  isOpen,
  openCycleOrders,
  onClose,
  onCreated,
  onOpenOrder,
}: AtlasInkooporderWizardDialogProps) {
  const [state, setState] = useState<WizardState>('idle');
  const [parsed, setParsed] = useState<AtlasParseResult | null>(null);
  const [items, setItems] = useState<EditableItem[]>([]);
  const [inkoopordernummer, setInkoopordernummer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [createdWo, setCreatedWo] = useState<{ id: string; number: string } | null>(null);
  const [afrondState, setAfrondState] = useState<Record<string, AfrondEntry>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setState('idle');
      setParsed(null);
      setItems([]);
      setInkoopordernummer('');
      setError(null);
      setIsDragOver(false);
      setCreatedWo(null);
      setAfrondState({});
    }
  }, [isOpen]);

  const totaal = useMemo(
    () => items.reduce((acc, it) => acc + (Number.isFinite(it.aantal) && Number.isFinite(it.verkoopprijsPerStuk) ? it.aantal * it.verkoopprijsPerStuk : 0), 0),
    [items],
  );

  const hasUnknownCode = items.some((it) => it.canonicalCode === null);
  const hasInvalidQty = items.some((it) => !Number.isFinite(it.aantal) || it.aantal <= 0);
  const hasInvalidPrice = items.some((it) => !Number.isFinite(it.verkoopprijsPerStuk) || it.verkoopprijsPerStuk < 0);
  const canSubmit =
    state === 'preview' &&
    items.length > 0 &&
    !!inkoopordernummer.trim() &&
    !hasUnknownCode &&
    !hasInvalidQty &&
    !hasInvalidPrice;

  if (!isOpen) return null;

  const handleFile = async (file: File) => {
    setError(null);
    setState('parsing');
    try {
      const result = await parseAtlasPdf(file);
      setParsed(result);
      setItems(toEditable(result.items));
      setInkoopordernummer(result.inkoopordernummer ?? '');
      setState('preview');
    } catch (err) {
      const msg =
        err instanceof AtlasInkooporderError
          ? `${err.code}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Parsen mislukt';
      setError(msg);
      setState('error');
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = '';
  };

  const updateItem = <K extends keyof EditableItem>(idx: number, key: K, value: EditableItem[K]) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        const updated: EditableItem = { ...item, [key]: value };
        if (key === 'productCode') {
          updated.canonicalCode = resolveAtlasProductCode(String(value));
        }
        return updated;
      }),
    );
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!canSubmit || !parsed) return;
    setState('submitting');
    setError(null);
    try {
      const result = await submitAtlasInkooporder({
        parentAccountId: accountId,
        inkoopordernummer: inkoopordernummer.trim(),
        pdfRetourMededeling: parsed.pdfRetourMededeling ?? undefined,
        pdfTotaal: parsed.pdfTotaalExBtw ?? undefined,
        items: items.map((it) => ({
          productCode: it.canonicalCode ?? it.productCode,
          aantal: it.aantal,
          verkoopprijsPerStuk: it.verkoopprijsPerStuk,
          omschrijving: it.omschrijving,
        })),
      });
      // Niet sluiten: door naar de afrondstap zodat de vorige order(s) van de
      // cyclus direct afgerond + gefactureerd kunnen worden.
      setCreatedWo({ id: result.workOrderId, number: result.workOrderNumber });
      setState('done');
      onCreated(result.workOrderId, result.workOrderNumber, result.itemsCreated);
    } catch (err) {
      const msg =
        err instanceof AtlasInkooporderError
          ? `${err.code}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Aanmaken mislukt';
      setError(msg);
      setState('preview');
    }
  };

  // Vorige orders van de cyclus die nog niet afgerond zijn (excl. de net
  // aangemaakte WO — die hoort open te blijven tot de volgende cyclus). Niet op
  // één status filteren: na "Markeer als gebracht" loopt een order door de
  // wasserij-statussen en moet hij hier zichtbaar blijven.
  const openOrders = (openCycleOrders ?? []).filter(
    (o) => isB2BCyclusOpenStatus(o.status) && o.id !== createdWo?.id,
  );

  const handleAfronden = async (orderId: string) => {
    setAfrondState((prev) => ({ ...prev, [orderId]: { status: 'busy' } }));
    try {
      const result = await afrondenEnFactureren(orderId);
      setAfrondState((prev) => ({
        ...prev,
        [orderId]: {
          status: 'done',
          invoiceNaam: result.invoiceNaam,
          sent: result.steps.sent ?? 'sent',
        },
      }));
    } catch (err) {
      const msg =
        err instanceof B2BCyclusError
          ? `${err.code}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Afronden mislukt';
      setAfrondState((prev) => ({ ...prev, [orderId]: { status: 'error', message: msg } }));
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Inkooporder verwerken — {accountName}</h2>
            <p className="text-xs text-slate-500 mt-1">
              Sleep de bestelbon (PDF) om te starten · AI parseert artikelregels · 1 klik tot Werk Order
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={state === 'submitting' || state === 'parsing'}
            className="text-sm text-slate-500 hover:text-slate-900 px-2"
            aria-label="Sluiten"
          >
            ✕
          </button>
        </div>

        {(state === 'idle' || state === 'parsing' || state === 'error') && (
          <div className="px-6 py-8">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              className={clsx(
                'border-2 border-dashed rounded-lg p-10 text-center transition-colors',
                state === 'parsing' && 'border-amber-400 bg-amber-50',
                isDragOver && state !== 'parsing' && 'border-amber-500 bg-amber-50',
                state !== 'parsing' && !isDragOver && 'border-slate-300 bg-slate-50',
              )}
            >
              {state === 'parsing' ? (
                <p className="text-sm text-amber-800">
                  PDF wordt geparseerd door Claude Haiku… (kan 5-10 sec duren)
                </p>
              ) : (
                <>
                  <p className="text-sm text-slate-700 font-medium">Sleep de AtlasKidtech-bestelbon hier</p>
                  <p className="text-xs text-slate-500 mt-1">of</p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 text-sm bg-amber-600 text-white px-3 py-1.5 rounded hover:bg-amber-700"
                  >
                    Kies een PDF…
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={handleSelectFile}
                  />
                </>
              )}
            </div>

            {error && (
              <p className="mt-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                {error}
              </p>
            )}
          </div>
        )}

        {(state === 'preview' || state === 'submitting') && parsed && (
          <>
            <div className="px-6 py-4 grid grid-cols-3 gap-4">
              <label className="block col-span-1">
                <span className="text-sm text-slate-700">
                  Inkoopordernummer<span className="text-red-600 ml-0.5">*</span>
                </span>
                <input
                  type="text"
                  value={inkoopordernummer}
                  onChange={(e) => setInkoopordernummer(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  disabled={state === 'submitting'}
                />
              </label>
              <div className="col-span-1 text-xs text-slate-600">
                <div className="font-medium text-slate-700 text-sm">Datum PDF</div>
                <div>{parsed.datum ?? '—'}</div>
              </div>
              <div className="col-span-1 text-xs text-slate-600">
                <div className="font-medium text-slate-700 text-sm">Totaal PDF (ex. BTW)</div>
                <div>{formatEur(parsed.pdfTotaalExBtw)}</div>
                <div className="mt-1 text-[11px] text-slate-500">
                  Berekend uit regels: <span className="font-mono">{formatEur(totaal)}</span>
                </div>
              </div>
            </div>

            {parsed.pdfRetourMededeling && (
              <div className="mx-6 mb-2 px-3 py-2 rounded bg-blue-50 border border-blue-200 text-xs text-blue-900">
                <strong>Retour-mededeling uit PDF:</strong> {parsed.pdfRetourMededeling}
              </div>
            )}

            <div className="px-6 pb-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 pr-2 w-16">Aantal</th>
                    <th className="text-left py-2 pr-2 w-44">Code</th>
                    <th className="text-left py-2 pr-2">Omschrijving</th>
                    <th className="text-right py-2 pr-2 w-24">PDF/st</th>
                    <th className="text-right py-2 pr-2 w-24">SF/st</th>
                    <th className="text-right py-2 pr-2 w-24">Δ</th>
                    <th className="text-right py-2 pr-2 w-24">Totaal</th>
                    <th className="text-right py-2 pr-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const unknown = it.canonicalCode === null;
                    const inferred = !!it.aiInferredCode;
                    const total = Number.isFinite(it.aantal) && Number.isFinite(it.verkoopprijsPerStuk)
                      ? it.aantal * it.verkoopprijsPerStuk
                      : 0;
                    const display = it.canonicalCode
                      ? ATLAS_PRODUCT_CODES[it.canonicalCode]
                      : null;
                    const rowBg = unknown
                      ? 'bg-red-50'
                      : inferred
                        ? 'bg-amber-50'
                        : '';
                    const deltaSpan = it.delta == null
                      ? '—'
                      : it.delta === 0
                        ? '0'
                        : (it.delta > 0 ? '+' : '') + formatEur(it.delta);
                    return (
                      <tr key={idx} className={clsx('border-b border-slate-100', rowBg)}>
                        <td className="py-1.5 pr-2">
                          <input
                            type="number"
                            min={1}
                            value={it.aantal}
                            onChange={(e) => updateItem(idx, 'aantal', Number(e.target.value))}
                            className="w-14 border border-slate-300 rounded px-1.5 py-1 text-xs text-right"
                            disabled={state === 'submitting'}
                          />
                        </td>
                        <td className="py-1.5 pr-2">
                          <input
                            type="text"
                            value={it.productCode}
                            onChange={(e) => updateItem(idx, 'productCode', e.target.value)}
                            className={clsx(
                              'w-full border rounded px-1.5 py-1 text-xs font-mono',
                              unknown ? 'border-red-400' : 'border-slate-300',
                            )}
                            disabled={state === 'submitting'}
                            title={unknown ? 'Onbekende AK-code' : it.canonicalCode ?? ''}
                          />
                          {inferred && !unknown && (
                            <div className="text-[10px] text-amber-700 mt-0.5" title="AI heeft code afgeleid uit omschrijving">
                              ⚠ AI-afgeleid
                            </div>
                          )}
                          {unknown && (
                            <div className="text-[10px] text-red-700 mt-0.5">Onbekende code</div>
                          )}
                        </td>
                        <td className="py-1.5 pr-2 text-slate-600">
                          <div className="truncate" title={it.omschrijving}>
                            {it.omschrijving || display || '—'}
                          </div>
                          {display && it.omschrijving && it.omschrijving !== display && (
                            <div className="text-[10px] text-slate-400 truncate">{display}</div>
                          )}
                        </td>
                        <td className="py-1.5 pr-2 text-right">
                          <input
                            type="number"
                            step="0.01"
                            min={0}
                            value={it.verkoopprijsPerStuk}
                            onChange={(e) => updateItem(idx, 'verkoopprijsPerStuk', Number(e.target.value))}
                            className="w-20 border border-slate-300 rounded px-1.5 py-1 text-xs text-right"
                            disabled={state === 'submitting'}
                          />
                        </td>
                        <td className="py-1.5 pr-2 text-right text-slate-500">
                          {it.sfPrijs != null ? formatEur(it.sfPrijs) : '—'}
                        </td>
                        <td className={clsx(
                          'py-1.5 pr-2 text-right',
                          it.delta == null ? 'text-slate-400' : Math.abs(it.delta) > 0.005 ? 'text-orange-700 font-medium' : 'text-slate-500',
                        )}>
                          {deltaSpan}
                        </td>
                        <td className="py-1.5 pr-2 text-right font-mono">{formatEur(total)}</td>
                        <td className="py-1.5 pr-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            disabled={state === 'submitting'}
                            className="text-slate-400 hover:text-red-700 text-sm"
                            title="Regel verwijderen"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {items.length === 0 && (
                <p className="text-xs text-slate-500 py-4 text-center">Geen regels meer — voeg een PDF toe of sluit het venster.</p>
              )}
            </div>

            {error && (
              <p className="mx-6 mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
            )}

            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                {hasUnknownCode && <span className="text-red-700 font-medium">Eén of meer regels hebben een onbekende code — corrigeer of verwijder.</span>}
                {!hasUnknownCode && hasInvalidQty && <span className="text-red-700">Aantal moet &gt; 0 zijn voor elke regel.</span>}
                {!hasUnknownCode && !hasInvalidQty && hasInvalidPrice && <span className="text-red-700">Prijs moet ≥ 0 zijn.</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-700">
                  Totaal: <span className="font-mono font-medium">{formatEur(totaal)}</span>
                </span>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={state === 'submitting'}
                  className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
                >
                  Sluiten
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className={clsx(
                    'text-sm font-medium px-3 py-1.5 rounded',
                    !canSubmit
                      ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-amber-600 text-white hover:bg-amber-700',
                  )}
                >
                  {state === 'submitting' ? 'Aanmaken…' : `Werk Order aanmaken (${items.length} regels)`}
                </button>
              </div>
            </div>
          </>
        )}

        {state === 'done' && createdWo && (
          <div className="px-6 py-6">
            <div className="rounded border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-sm text-emerald-900 font-medium">
                Nieuwe order {createdWo.number} aangemaakt.
              </p>
              {onOpenOrder && (
                <button
                  type="button"
                  onClick={() => onOpenOrder(createdWo.id)}
                  className="mt-1 text-xs text-emerald-800 underline hover:text-emerald-900"
                >
                  Open {createdWo.number}
                </button>
              )}
            </div>

            <div className="mt-5">
              <h3 className="text-sm font-semibold text-slate-900">
                Openstaande vorige order(s)
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Deze orders zijn opgehaald bij de vorige cyclus en kunnen nu afgerond +
                gefactureerd worden. Afronden verstuurt de factuur direct naar de klant.
                Print het retourlabel en plak het op de zak die mee teruggaat — de klant
                boekt de ontvangst op dat inkoopnummer in.
              </p>

              {openOrders.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">
                  Geen openstaande vorige orders — alles is bij.
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-slate-100 border border-slate-200 rounded">
                  {openOrders.map((order) => {
                    const entry = afrondState[order.id];
                    return (
                      <li key={order.id} className="px-4 py-3 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900">{order.reference}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700">
                              {order.status}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {new Date(order.createdDate).toLocaleDateString('nl-NL')} · {formatEur(order.total)}
                          </div>
                          {entry?.status === 'done' && (
                            <div className="text-xs text-emerald-700 mt-1">
                              Afgerond — factuur {entry.invoiceNaam ?? 'aangemaakt'}
                              {entry.sent === 'skipped-already-sent' ? ' (was al verstuurd)' : ' verstuurd'}
                            </div>
                          )}
                          {entry?.status === 'error' && (
                            <div className="text-xs text-red-700 mt-1">{entry.message}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                        <B2BRetourlabelButton
                          werkOrderId={order.id}
                          orderReferentie={order.reference}
                        />
                        {entry?.status !== 'done' && (
                          <button
                            type="button"
                            onClick={() => void handleAfronden(order.id)}
                            disabled={entry?.status === 'busy'}
                            className={clsx(
                              'text-xs font-medium px-3 py-1.5 rounded flex-shrink-0',
                              entry?.status === 'busy'
                                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                                : 'bg-amber-600 text-white hover:bg-amber-700',
                            )}
                          >
                            {entry?.status === 'busy'
                              ? 'Bezig…'
                              : entry?.status === 'error'
                                ? 'Opnieuw proberen'
                                : 'Afronden + factureren'}
                          </button>
                        )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-300 rounded"
              >
                Sluiten
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
